import { workoutEnvironmentRanges } from '../../utils/workoutEnvironmentRanges';
import { NmLoader } from '../../components/NmLoader';
import { workoutDisplayDigits as displayDigits } from '../../utils/workoutNumberFormat';
import { runningStatsPresentation } from '../../utils/runningStatsPresentation';
import { runningSelectionMetrics } from '../../utils/runningSelectionMetrics';
import { runningDisplayStream } from '../../utils/runningWorkoutMetrics';
import { computeRangeStats } from '../../utils/workoutStream';
import { WorkoutZoneDistribution } from '../../components/charts/WorkoutZoneDistribution';
import { workoutMetricLabel } from '../../utils/workoutMetricLabel';
import { selectedWorkoutPeaks } from '../../utils/selectedWorkoutPeaks';
import { WorkoutAnalysisDetails } from './WorkoutAnalysisDetails';
import { secToHMS } from '../../utils/secToHMS';
import { formatWorkoutDistanceM } from '../../utils/workoutStream';
import { workoutStressLabel } from '../../utils/workoutStressLabel';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { isRunningWorkout } from '../../utils/runningWorkoutMetrics';
import { useTranslation } from 'react-i18next';
import { IoChevronBackOutline, IoChevronForwardOutline, IoSettingsOutline } from 'react-icons/io5';
import { useChartLandscapeFullscreen } from '../../hooks/useChartLandscapeFullscreen';
import type { WorkoutDetail } from '../../services/api';
import { workouts } from '../../services/api';
import { GpsMap, type MapTileStyle } from '../../components/workout/GpsMap';
import { parseStreamData, computeRangeDistanceM, normalizeApiRangeDistanceM } from '../../utils/workoutStream';
import { buildStructureTimeline, normalizeWorkoutStreamTimeline } from '../../utils/workoutStructure';
import { useAthleteMetrics } from '../../hooks/useAthleteMetrics';
import { lapDistanceM } from '../../utils/comparison/lapSegments';
import { MobileWorkoutStreamChart, type MobileWorkoutStreamChartHandle, type StreamSeriesKey } from './MobileWorkoutStreamChart';
import { WorkoutMapBottomSheet, type HighlightStats } from './WorkoutMapBottomSheet';
import { MobileSheet } from '../../components/MobileSheet';
import { WorkoutMapDataPicker } from './WorkoutMapDataPicker';
import { NmToggle } from '../../components/NmToggle';
import {
  findStreamIndicesForModalItem,
  findStreamIndicesForLap,
  formatPeakSelectionTitle,
} from './workoutPeakHelpers';
import { WORKOUT_DETAIL_SHEET_Z } from './workoutOverlayZIndex';
import { MobileWorkoutPdcChart } from './MobileWorkoutPdcChart';
import { workoutHasPeakData } from './useWorkoutPdcChart';

type ChartSelection = {
  start: number;
  end: number;
  title?: string;
  sourceTab?: number;
  sourceItem?: Record<string, unknown>;
};

const AUTO_ZOOM_ON_SELECTION_KEY = 'vo2_wm_auto_zoom_selection';
const SHOW_STRUCTURE_KEY = 'vo2_wm_show_structure';
/** Seconds per tap on structure shift buttons. */
const STRUCTURE_OFFSET_STEP_SEC = 30;

function structureOffsetKey(workoutId: number): string {
  return `vo2_wm_structure_offset_${workoutId}`;
}

function readStructureOffset(workoutId: number): number {
  try {
    const raw = localStorage.getItem(structureOffsetKey(workoutId));
    if (raw == null) return 0;
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

function persistStructureOffset(workoutId: number, offsetSec: number): void {
  try {
    if (offsetSec === 0) {
      localStorage.removeItem(structureOffsetKey(workoutId));
    } else {
      localStorage.setItem(structureOffsetKey(workoutId), String(offsetSec));
    }
  } catch {
    /* ignore */
  }
}

function formatStructureOffset(sec: number): string {
  const sign = sec >= 0 ? '+' : '−';
  const abs = Math.abs(Math.round(sec));
  const h = Math.floor(abs / 3600);
  const m = Math.floor((abs % 3600) / 60);
  const s = abs % 60;
  if (h > 0) return `${sign}${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${sign}${m}:${String(s).padStart(2, '0')}`;
}

function readAutoZoomOnSelection(): boolean {
  try {
    const v = localStorage.getItem(AUTO_ZOOM_ON_SELECTION_KEY);
    return v === null ? true : v === '1';
  } catch {
    return true;
  }
}

function readShowStructure(): boolean {
  try {
    const v = localStorage.getItem(SHOW_STRUCTURE_KEY);
    return v === '1';
  } catch {
    return false;
  }
}

interface Props {
  workout: WorkoutDetail;
  workoutId: number;
  userId?: number;
  streamRaw: unknown;
  streamLoading: boolean;
  streamError: string | null;
  onStreamRetry?: () => void;
  onWorkoutClick?: (workoutId: number) => void;
}

function coordsFromStream(stream: ReturnType<typeof parseStreamData>): [number, number][] {
  const out: [number, number][] = [];
  stream.forEach((pt) => {
    if (pt.positionLat == null || pt.positionLong == null) return;
    const lat = Number(pt.positionLat);
    const lng = Number(pt.positionLong);
    if (!Number.isFinite(lat) || !Number.isFinite(lng) || (lat === 0 && lng === 0)) return;
    out.push([lat, lng]);
  });
  return out;
}

function sliceCoords(
  stream: ReturnType<typeof parseStreamData>,
  startIdx: number,
  endIdx: number,
): [number, number][] {
  const a = Math.min(startIdx, endIdx);
  const b = Math.max(startIdx, endIdx);
  return coordsFromStream(stream.slice(a, b + 1));
}

function ChartFullscreenIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" aria-hidden>
      <path d="M8 3H5a2 2 0 0 0-2 2v3M21 8V5a2 2 0 0 0-2-2h-3M3 16v3a2 2 0 0 0 2 2h3M16 21h3a2 2 0 0 0 2-2v-3" />
    </svg>
  );
}

export function MobileWorkoutMapTab({
  workout,
  workoutId,
  userId,
  streamRaw,
  streamLoading,
  streamError,
  onStreamRetry,
  onWorkoutClick,
}: Props) {
  const { t } = useTranslation();
  const { ftp } = useAthleteMetrics(userId ?? null);
  const hasPeaks = workoutHasPeakData(workout);

  const [mapStyle, setMapStyle] = useState<MapTileStyle>('outdoor');
  const [showMapSettings, setShowMapSettings] = useState(false);
  const [showChartSettings, setShowChartSettings] = useState(false);
  const [dataPickerTab, setDataPickerTab] = useState(0);

  const [visible, setVisible] = useState<Record<StreamSeriesKey, boolean>>({
    power: true,
    hr: true,
    cadence: true,
    speed: true,
    pace: false,
    elevation: true,
    temp: false,
  });
  const [smoothLevel, setSmoothLevel] = useState(30);
  const [showStructure, setShowStructure] = useState(readShowStructure);
  const [structureOffsetSec, setStructureOffsetSec] = useState(() => readStructureOffset(workoutId));
  const chartRef = useRef<MobileWorkoutStreamChartHandle>(null);
  const fsChartRef = useRef<MobileWorkoutStreamChartHandle>(null);
  const mapTabRef = useRef<HTMLDivElement>(null);
  const mapSectionRef = useRef<HTMLDivElement>(null);
  const { open: fsOpen, enter: enterFs, exit: exitFs, overlayRef } = useChartLandscapeFullscreen();

  const [selection, setSelection] = useState<ChartSelection | null>(null);
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const [mode, setMode] = useState<1 | 3>(1);
  const [highlight, setHighlight] = useState<HighlightStats | null>(null);
  const [rangeLoading, setRangeLoading] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [sheetLevel, setSheetLevel] = useState<0 | 1 | 2>(1);
  const [mapFocusToken, setMapFocusToken] = useState(0);
  const [selectedDataKey, setSelectedDataKey] = useState<string | null>(null);
  const [lapStatsRequest, setLapStatsRequest] = useState(0);
  const [autoZoomOnSelection, setAutoZoomOnSelection] = useState(readAutoZoomOnSelection);
  const lastMapFocusSelectionRef = useRef<string | null>(null);
  const rangeRequestRef = useRef(0);

  /** Bring the map itself into view after a peaks/laps picker selection. */
  const scrollToMapSection = useCallback(() => {
    requestAnimationFrame(() => {
      mapSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  const stream = useMemo(() => parseStreamData(streamRaw), [streamRaw]);
  const environmentRanges = useMemo(() => workoutEnvironmentRanges(workout.details?.fit_metrics, stream, selection), [workout.details?.fit_metrics, stream, selection]);
  const fullRoute = useMemo(() => coordsFromStream(stream), [stream]);

  const totalTimeSec = useMemo(() => {
    const fromWorkout = Number(workout.total_time ?? 0);
    if (fromWorkout > 0) return fromWorkout;
    const last = stream[stream.length - 1]?.timeSeconds;
    return last != null && Number.isFinite(last) ? last : 0;
  }, [workout.total_time, stream]);

  /** Normalized elapsed axis — must match MobileWorkoutStreamChart for lap highlight indices. */
  const timelineStream = useMemo(
    () => normalizeWorkoutStreamTimeline(stream, totalTimeSec || 0),
    [stream, totalTimeSec],
  );

  const plannedTotalSec = useMemo(() => {
    const planned = Number(workout.total_time_planned ?? 0);
    if (planned > 0) return planned;
    return totalTimeSec;
  }, [workout.total_time_planned, totalTimeSec]);

  const structureSegments = useMemo(
    () => buildStructureTimeline(workout.structure ?? null, plannedTotalSec),
    [workout.structure, plannedTotalSec],
  );
  const hasStructure = structureSegments.length > 0;

  useEffect(() => {
    setStructureOffsetSec(readStructureOffset(workoutId));
  }, [workoutId]);

  const shiftStructure = useCallback((deltaSec: number) => {
    setStructureOffsetSec((prev) => {
      const next = prev + deltaSec;
      persistStructureOffset(workoutId, next);
      return next;
    });
  }, [workoutId]);

  const resetStructureOffset = useCallback(() => {
    setStructureOffsetSec(0);
    persistStructureOffset(workoutId, 0);
  }, [workoutId]);

  const highlightRoute = useMemo(() => {
    if (!selection || stream.length < 2) return undefined;
    const a = Math.min(selection.start, selection.end);
    const b = Math.max(selection.start, selection.end);
    if (a === b) return undefined;
    return sliceCoords(stream, a, b);
  }, [selection, stream]);

  const hoverPosition = useMemo((): [number, number] | null => {
    if (hoverIdx == null || !stream[hoverIdx]) return null;
    const pt = stream[hoverIdx];
    if (pt.positionLat == null || pt.positionLong == null) return null;
    return [Number(pt.positionLat), Number(pt.positionLong)];
  }, [hoverIdx, stream]);

  const applySelection = useCallback(async (
    startIdx: number,
    endIdx: number,
    title?: string,
    sourceLapItem?: Record<string, unknown>,
  ) => {
    const requestId = ++rangeRequestRef.current;
    let a = Math.min(startIdx, endIdx);
    let b = Math.max(startIdx, endIdx);
    if (a === b && stream.length > 1) {
      b = Math.min(a + 1, stream.length - 1);
    }
    if (a === b) return;

    const startTs = stream[a]?.timestamp;
    const endTs = stream[b]?.timestamp;
    const rangeTitle = title ?? t('workoutDetail.map.selectedRange');
    const elapsedSec = Math.max(
      0,
      (timelineStream[b]?.timeSeconds ?? stream[b].timeSeconds)
        - (timelineStream[a]?.timeSeconds ?? stream[a].timeSeconds),
    );
    const displayStream = runningDisplayStream(stream, workout);
    const localStats = computeRangeStats(displayStream, a, b);
    const runningFallback: Partial<HighlightStats> = isRunningWorkout(workout) && localStats ? {
      running_metrics: runningSelectionMetrics({}, localStats, displayStream.some(point => point.cadenceUnit === 'spm')),
      min_max_avg: { heart_rate: localStats.heartRate ?? undefined, power: localStats.power ?? undefined, cadence: localStats.cadence ?? undefined, speed: localStats.speed ?? undefined },
    } : {};
    const streamDistanceM = computeRangeDistanceM(stream, a, b);
    const lapDistanceMeters = sourceLapItem
      ? lapDistanceM(sourceLapItem as Parameters<typeof lapDistanceM>[0])
      : null;

    const mergeDistance = (calc?: Record<string, unknown> | null) => {
      const elapsed = startTs != null && endTs != null ? endTs - startTs : elapsedSec;
      return normalizeApiRangeDistanceM(calc?.distance, elapsed)
        ?? (streamDistanceM != null ? normalizeApiRangeDistanceM(streamDistanceM, elapsedSec) : null)
        ?? lapDistanceMeters
        ?? undefined;
    };

    const mergeTss = (calc?: Record<string, unknown> | null) => {
      const v = calc?.tss ?? calc?.tss_actual;
      return v != null ? Number(v) : undefined;
    };

    if (startTs == null || endTs == null) {
      setMode(3);
      setHighlight({
        ...runningFallback,
        title: rangeTitle,
        total_time: elapsedSec > 0 ? elapsedSec : undefined,
        distance: mergeDistance(),
        tss: sourceLapItem ? mergeTss(sourceLapItem) : undefined,
        min_max_avg: runningFallback.min_max_avg,
      });
      setRangeLoading(false);
      return;
    }

    if (!userId) {
      setMode(3);
      setHighlight({
        ...runningFallback,
        title: rangeTitle,
        total_time: elapsedSec > 0 ? elapsedSec : undefined,
        distance: mergeDistance(),
        tss: sourceLapItem ? mergeTss(sourceLapItem) : undefined,
      });
      setRangeLoading(false);
      return;
    }

    setRangeLoading(true);
    try {
      const res = await workouts.calculateSelectedRange(workoutId, userId, startTs, endTs);
      if (requestId !== rangeRequestRef.current) return;
      const calc = (res.data?.data?.calculation ?? res.data?.calculation ?? res.data) as Record<string, unknown>;
      setHighlight({
        ...runningFallback,
        ...(calc as HighlightStats),
        min_max_avg: isRunningWorkout(workout) ? runningFallback.min_max_avg ?? (calc as HighlightStats).min_max_avg : (calc as HighlightStats).min_max_avg,
        running_metrics: isRunningWorkout(workout) && localStats ? runningSelectionMetrics(calc as Parameters<typeof runningSelectionMetrics>[0], localStats, displayStream.some(point => point.cadenceUnit === 'spm')) : undefined,
        title: rangeTitle,
        total_time: elapsedSec > 0 ? elapsedSec : undefined,
        distance: mergeDistance(calc),
        tss: mergeTss(calc) ?? mergeTss(sourceLapItem),
      });
      setMode(3);
    } catch {
      if (requestId !== rangeRequestRef.current) return;
      setHighlight({
        ...runningFallback,
        title: rangeTitle,
        total_time: elapsedSec > 0 ? elapsedSec : undefined,
        distance: mergeDistance(),
        tss: mergeTss(sourceLapItem),
      });
      setMode(3);
    } finally {
      if (requestId === rangeRequestRef.current) setRangeLoading(false);
    }
  }, [stream, timelineStream, userId, workoutId, t, workout]);

  useEffect(() => {
    ++rangeRequestRef.current;
    if (!selection) return;
    const a = Math.min(selection.start, selection.end);
    const b = Math.max(selection.start, selection.end);
    if (a === b) return;

    const elapsedSec = Math.max(
      0,
      (timelineStream[b]?.timeSeconds ?? 0) - (timelineStream[a]?.timeSeconds ?? 0),
    );
    const displayStream = runningDisplayStream(stream, workout);
    const localStats = computeRangeStats(displayStream, a, b);
    setMode(3);
    setRangeLoading(true);
    setHighlight({
      ...(isRunningWorkout(workout) && localStats ? {
        running_metrics: runningSelectionMetrics({}, localStats, displayStream.some(point => point.cadenceUnit === 'spm')),
        min_max_avg: { heart_rate: localStats.heartRate ?? undefined, power: localStats.power ?? undefined, cadence: localStats.cadence ?? undefined, speed: localStats.speed ?? undefined },
      } : {}),
      title: selection.title ?? t('workoutDetail.map.selectedRange'),
      total_time: elapsedSec || undefined,
      distance: computeRangeDistanceM(stream, a, b) ?? undefined,
    });

    const timer = window.setTimeout(
      () => void applySelection(
        a,
        b,
        selection.title,
        selection.sourceTab === 0 ? selection.sourceItem : undefined,
      ),
      400,
    );
    return () => { window.clearTimeout(timer); ++rangeRequestRef.current; };
  }, [selection, applySelection, stream, timelineStream, t, workout]);

  useEffect(() => {
    if (!selection || !autoZoomOnSelection) return;
    const a = Math.min(selection.start, selection.end);
    const b = Math.max(selection.start, selection.end);
    if (a === b) return;
    const key = `${a}-${b}`;
    if (lastMapFocusSelectionRef.current === key) return;
    const timer = window.setTimeout(() => {
      lastMapFocusSelectionRef.current = key;
      chartRef.current?.zoomToSelection(a, b);
      fsChartRef.current?.zoomToSelection(a, b);
      setMapFocusToken((n) => n + 1);
    }, 400);
    return () => window.clearTimeout(timer);
  }, [selection, autoZoomOnSelection]);

  const rangePeaks = useMemo(() => selection ? selectedWorkoutPeaks(timelineStream.slice(Math.min(selection.start, selection.end), Math.max(selection.start, selection.end) + 1)) : undefined, [selection, timelineStream]);

  const clearSelection = () => {
    ++rangeRequestRef.current;
    chartRef.current?.resetZoom();
    fsChartRef.current?.resetZoom();
    setSelection(null);
    setHighlight(null);
    setMode(1);
    setRangeLoading(false);
    setSelectionMode(false);
    setSelectedDataKey(null);
    lastMapFocusSelectionRef.current = null;
  };

  const handleAutoZoomChange = (on: boolean) => {
    setAutoZoomOnSelection(on);
    try {
      localStorage.setItem(AUTO_ZOOM_ON_SELECTION_KEY, on ? '1' : '0');
    } catch {
      /* ignore */
    }
  };

  const handleShowStructureChange = (on: boolean) => {
    setShowStructure(on);
    try {
      localStorage.setItem(SHOW_STRUCTURE_KEY, on ? '1' : '0');
    } catch {
      /* ignore */
    }
  };

  const rowTitle = useCallback(
    (item: Record<string, unknown>, idx: number, tab: number) => {
      if (tab === 0) {
        return t('workoutDetail.map.lapLabel', { n: Number(item.lap ?? idx) + 1 });
      }
      return formatPeakSelectionTitle(item, t);
    },
    [t],
  );

  const handleDataRowClick = useCallback(
    (item: Record<string, unknown>, idx: number, tab: number) => {
      if (streamLoading || !timelineStream.length) return;

      const workoutDurationSec = Number(workout.total_time ?? 0) || timelineStream[timelineStream.length - 1]?.timeSeconds || 7200;
      const indices = tab === 0
        ? findStreamIndicesForLap(
            (workout.details?.lap_data ?? []) as Parameters<typeof findStreamIndicesForLap>[0],
            idx,
            timelineStream,
            workoutDurationSec,
          )
        : findStreamIndicesForModalItem(timelineStream, item);
      if (!indices) return;

      const { startIdx, endIdx } = indices;
      const title = rowTitle(item, idx, tab);

      setSelectedDataKey(`${tab}-${idx}`);
      setSelection({ start: startIdx, end: endIdx, title, sourceTab: tab, sourceItem: tab === 0 ? item : undefined });
      setLapStatsRequest(request => request + 1);
    },
    [timelineStream, streamLoading, rowTitle, workout, scrollToMapSection],
  );

  const mapTypes: { id: MapTileStyle; label: string }[] = [
    { id: 'street', label: t('workoutDetail.map.styleStreet') },
    { id: 'light', label: t('workoutDetail.map.styleLight') },
    { id: 'outdoor', label: t('workoutDetail.map.styleOutdoor') },
    { id: 'dark', label: t('workoutDetail.map.styleDark') },
  ];

  const powerAvailable = !isRunningWorkout(workout) || runningStatsPresentation(workout).power != null || stream.some(point => point.power != null && point.power > 0);
  const seriesOptions: { key: StreamSeriesKey; label: string; color: string }[] = [
    ...(powerAvailable ? [{ key: 'power' as const, label: t('workoutDetail.map.seriesPower'), color: '#da17d8' }] : []),
    { key: 'speed', label: t('workoutDetail.map.seriesSpeed'), color: '#237e0d' },
    ...(isRunningWorkout(workout) ? [{ key: 'pace' as const, label: t('runningMetrics.paceLabel'), color: '#38bdf8' }] : []),
    { key: 'cadence', label: t(isRunningWorkout(workout) ? 'runningMetrics.cadence' : 'workoutDetail.map.seriesCadence'), color: '#ebb550' },
    { key: 'hr', label: t('workoutDetail.map.seriesHr'), color: '#9d3e41' },
    { key: 'elevation', label: t('workoutDetail.map.seriesElevation'), color: '#929292' },
    { key: 'temp', label: t('workoutDetail.map.seriesTemp'), color: '#2011c9' },
  ];

  const onChartSelectionChange = useCallback((sel: { start: number; end: number } | null) => {
    if (!sel) {
      setSelection(null);
      setSelectionMode(false);
      return;
    }
    setSelectedDataKey(null);
    setSelection({ start: sel.start, end: sel.end });
  }, []);

  const streamChartReady = !streamLoading && !streamError;

  const renderStreamChart = (ref: React.Ref<MobileWorkoutStreamChartHandle>) => (
    <MobileWorkoutStreamChart
      ref={ref}
      rawStream={streamRaw}
      running={isRunningWorkout(workout)}
      visible={{ ...visible, power: visible.power && powerAvailable }}
      smoothLevel={smoothLevel}
      selection={selection}
      selectionMode={selectionMode}
      showStructure={showStructure && hasStructure}
      structureSegments={structureSegments}
      structureOffsetSec={structureOffsetSec}
      totalTime={totalTimeSec}
      ftp={ftp}
      onSelectionChange={onChartSelectionChange}
      onClearSelection={clearSelection}
      onSelectionModeChange={setSelectionMode}
      onHoverIndex={setHoverIdx}
    />
  );

  const renderChartOverlayActions = (showExpand = false) => (
    <>
      <button
        type="button"
        className="wm-chart-overlay-action wm-chart-overlay-action--settings"
        onClick={() => setShowChartSettings(true)}
        aria-label={t('workoutDetail.map.chartSettings')}
        title={t('workoutDetail.map.chartSettings')}
      >
        <IoSettingsOutline aria-hidden />
      </button>
      {showExpand ? (
        <button
          type="button"
          className="wm-chart-overlay-action wm-chart-overlay-action--fullscreen"
          onClick={enterFs}
          aria-label={t('workoutDetail.map.expandFullscreen')}
          title={t('workoutDetail.map.expandFullscreen')}
        >
          <ChartFullscreenIcon />
        </button>
      ) : null}
    </>
  );

  const streamFullscreenOverlay = fsOpen && streamChartReady
    ? createPortal(
        <div
          ref={overlayRef}
          className="pdc-fullscreen-overlay mobile-fullscreen-panel vo2-slide-up-fullscreen wm-stream-fullscreen"
          role="dialog"
          aria-modal="true"
          aria-label={t('workoutDetail.map.expandFullscreen')}
        >
          <header className="pdc-fullscreen-overlay__header">
            <button
              type="button"
              className="pdc-fullscreen-overlay__close pdc-chart__icon-btn"
              onClick={() => void exitFs()}
              aria-label={t('pdcChart.close')}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
            </button>
            <span className="pdc-fullscreen-overlay__spacer" aria-hidden />
          </header>
          <p className="pdc-fullscreen-overlay__hint">{t('pdcChart.rotateHint')}</p>
          <div className="pdc-fullscreen-overlay__body wm-stream-fullscreen__body">
            <div className="wm-map-tab__chart wm-map-tab__chart--fullscreen wm-map-tab__chart--with-actions">
              {renderChartOverlayActions()}
              {renderStreamChart(fsChartRef)}
            </div>
          </div>
        </div>,
        document.body,
      )
    : null;

  return (
    <div className="wm-map-tab" ref={mapTabRef}>
      <div className="wa-overview">
        {[
          [t('workoutDetail.metrics.time'), secToHMS(Number(workout.total_time ?? 0))],
          [t('workoutDetail.metrics.distance'), formatWorkoutDistanceM(Number(workout.distance ?? 0))],
          [workoutStressLabel(workout), workout.tss_actual ?? '—'],
          [workoutMetricLabel('IF'), workout.if_actual ?? '—'],
        ].map(([label, value]) => <div className="wa-overview__metric" key={String(label)}><span>{label}</span><strong className="ltr-island">{displayDigits(String(value))}</strong></div>)}
      </div>

      <div className="wm-map-tab__map" ref={mapSectionRef}>
        <button type="button" className="wm-map-tab__settings" onClick={() => setShowMapSettings(true)} aria-label={t('workoutDetail.map.mapSettings')}>
          <IoSettingsOutline aria-hidden />
        </button>
        <GpsMap
          gpsRoute={fullRoute}
          gpsSelection={highlightRoute}
          hoverPosition={hoverPosition}
          mapStyle={mapStyle}
          fitKey={`${workoutId}-${fullRoute.length}`}
          focusToken={mapFocusToken}
        />
      </div>

      <div className="wm-map-tab__chart-section">
        <div className="wm-map-tab__chart-header">
          <h3 className="wm-map-tab__chart-title">{t('workoutDetail.map.timelineTitle')}</h3>
          <div className="wm-map-tab__chart-header-actions">{renderChartOverlayActions(streamChartReady)}</div>
        </div>
        <div className="wm-map-tab__chart">
          {streamLoading ? (
            <div className="wm-map-tab__chart-loading"><NmLoader size="sm" inline label={t('workoutDetail.loading')} /></div>
          ) : streamError ? (
            <div className="wm-map-tab__chart-loading">
              <span>{streamError}</span>
              {onStreamRetry && (
                <button type="button" className="wm-map-tab__retry" onClick={onStreamRetry}>
                  {t('workoutDetail.retry')}
                </button>
              )}
            </div>
          ) : (
            renderStreamChart(chartRef)
          )}
        </div>
      </div>

      {streamFullscreenOverlay}

      <WorkoutAnalysisDetails
        statsTitle={selection?.title}
        statsRequest={lapStatsRequest}
        selected={selection ? `${t('workoutDetail.map.selectedRange')} · ${secToHMS(Math.max(0, (timelineStream[Math.max(selection.start, selection.end)]?.timeSeconds ?? 0) - (timelineStream[Math.min(selection.start, selection.end)]?.timeSeconds ?? 0)))}` : null}
        onClear={clearSelection}
        statistics={<WorkoutMapBottomSheet environmentRanges={environmentRanges} embedded hideZones
        mode={mode}
        workout={workout}
        highlight={highlight}
        loading={rangeLoading}
        rangeStartSec={selection ? timelineStream[Math.min(selection.start, selection.end)]?.timeSeconds : undefined}
        rangeEndSec={selection ? timelineStream[Math.max(selection.start, selection.end)]?.timeSeconds : undefined}
        sheetLevel={sheetLevel}
        onToggleSheet={() => setSheetLevel((level) => (level === 0 ? 1 : 0))}
      />}
        laps={<WorkoutMapDataPicker embedded
        workout={workout}
        stream={timelineStream}
        streamLoading={streamLoading}
        activeTab={dataPickerTab}
        onTabChange={setDataPickerTab}
        selectedKey={selectedDataKey}
        onRowClick={handleDataRowClick}
      />}
      />

      <div className="wm-map-tab__pdc workout-curve-standalone">
          {hasPeaks ? <MobileWorkoutPdcChart data={workout} userId={userId} variant="summary" onWorkoutClick={onWorkoutClick} onCurrentRangeClick={item => handleDataRowClick(item, 0, 1)} peaks={rangePeaks} currentLabel={selection ? t('workoutDetail.map.selectedRange') : undefined} /> : <p>{t('workoutDetail.peaks.empty')}</p>}
        </div>

      {!selection && ((workout.details?.zones?.hr_zones?.length ?? 0) > 0 || (workout.details?.zones?.pw_zones?.length ?? 0) > 0) && <div className="workout-zone-cards">
        <WorkoutZoneDistribution title={t('workoutDetail.summary.hrZones')} zones={workout.details?.zones?.hr_zones ?? []} kind="heart" />
        <WorkoutZoneDistribution title={t('workoutDetail.summary.pwZones')} zones={workout.details?.zones?.pw_zones ?? []} kind="power" />
      </div>}

      <MobileSheet
        open={showMapSettings}
        onClose={() => setShowMapSettings(false)}
        title={t('workoutDetail.map.mapSettings')}
        zIndex={WORKOUT_DETAIL_SHEET_Z}
      >
        <div className="wm-settings-grid">
          {mapTypes.map((m) => (
            <button
              key={m.id}
              type="button"
              className={`wm-settings-chip${mapStyle === m.id ? ' wm-settings-chip--active' : ''}`}
              onClick={() => { setMapStyle(m.id); setShowMapSettings(false); }}
            >
              {m.label}
            </button>
          ))}
        </div>
      </MobileSheet>

      <MobileSheet
        open={showChartSettings}
        onClose={() => setShowChartSettings(false)}
        title={t('workoutDetail.map.chartSettings')}
        zIndex={WORKOUT_DETAIL_SHEET_Z}
        sheetClassName="wm-chart-settings-sheet"
      >
        <div className="wm-map-settings-toggle">
          <NmToggle
            checked={autoZoomOnSelection}
            onChange={handleAutoZoomChange}
            label={t('workoutDetail.map.autoZoomOnSelection')}
          />
        </div>
        <label className="wm-smooth-label">
          {t('workoutDetail.map.smoothLevel', { pct: smoothLevel })}
          <input
            type="range"
            min={0}
            max={80}
            value={smoothLevel}
            onChange={(e) => setSmoothLevel(Number(e.target.value))}
          />
        </label>
        <div className="wm-settings-grid">
          {seriesOptions.map((s) => (
            <button
              key={s.key}
              type="button"
              className={`wm-settings-chip${visible[s.key] ? ' wm-settings-chip--active' : ''}`}
              style={{ borderColor: visible[s.key] ? s.color : undefined }}
              onClick={() => setVisible((v) => ({ ...v, [s.key]: !v[s.key] }))}
            >
              {s.label}
            </button>
          ))}
          {hasStructure ? (
            <button
              type="button"
              className={`wm-settings-chip${showStructure ? ' wm-settings-chip--active' : ''}`}
              style={{ borderColor: showStructure ? 'var(--text-accent)' : undefined }}
              onClick={() => handleShowStructureChange(!showStructure)}
            >
              {t('workoutDetail.map.seriesStructure')}
            </button>
          ) : null}
        </div>
        {hasStructure && showStructure ? (
          <div className="wm-chart-settings__structure">
            <span className="wm-chart-settings__label">{t('workoutDetail.map.structureAlignment')}</span>
            <div className="wm-chart-toolbar__structure ltr-island">
              <button type="button" className="wm-chart-toolbar__btn" aria-label={t('workoutDetail.map.structureShiftLeft')} onClick={() => shiftStructure(-STRUCTURE_OFFSET_STEP_SEC)}><IoChevronBackOutline aria-hidden /></button>
              <button type="button" className={`wm-chart-toolbar__center${structureOffsetSec !== 0 ? ' wm-chart-toolbar__center--active' : ''}`} aria-label={structureOffsetSec !== 0 ? t('workoutDetail.map.structureOffsetReset') : undefined} onClick={structureOffsetSec !== 0 ? resetStructureOffset : undefined} disabled={structureOffsetSec === 0}>
                {structureOffsetSec !== 0 ? formatStructureOffset(structureOffsetSec) : t('workoutDetail.map.structureAlignHint')}
              </button>
              <button type="button" className="wm-chart-toolbar__btn" aria-label={t('workoutDetail.map.structureShiftRight')} onClick={() => shiftStructure(STRUCTURE_OFFSET_STEP_SEC)}><IoChevronForwardOutline aria-hidden /></button>
            </div>
          </div>
        ) : null}
      </MobileSheet>
    </div>
  );
}
